"use client"

import { LoggerMolecule } from "@/instrumentation/logger/logger-molecule"
import { type EventContext, Flow } from "@/instrumentation/logger/types"
import * as amplitude from "@amplitude/analytics-browser"
import { useMolecule } from "bunshi/react"
import { useEffect } from "react"
import { BannerSection } from "./banner-section"
import { CollectionsSection } from "./collections-section"
import { CollectorsSection } from "./collectors-section"
import { ImageSection } from "./image-section"
import { InfoSection } from "./info-section"
import { ListSection } from "./list-section"
import { ModalSection } from "./modal-section"
import { SliderSection } from "./slider-section"

interface PageSection {
  id: string
  type:
    | "info"
    | "list"
    | "slider"
    | "collections"
    | "video"
    | "collectors"
    | "banner"
    | "image"
    | "modal"
  order: number
  isVisible: boolean
  props: Record<string, unknown>
  styles?: {
    backgroundColor?: string
    textColor?: string
    padding?: string
    margin?: string
  }
}

interface MerchantData {
  name?: string
  description?: string
  logo?: string
  website?: string
  twitter?: string
  discord?: string
  instagram?: string
  youtube?: string
}

interface CollectionStats {
  totalVolume?: string
  totalItems?: number
  percentListed?: string
}

interface SectionRendererProps {
  section: PageSection
  merchantBlockchain?: string // Fallback blockchain for collections/slides
  merchantData?: MerchantData // For banner section fallbacks
  collectionStats?: CollectionStats // For banner section stats
  statsLoading?: boolean
  statsError?: Error | null
  brandTheme?: {
    backgroundColor?: string
    textColor?: string
  }
}

export function SectionRenderer({
  section,
  merchantBlockchain,
  merchantData,
  collectionStats,
  statsLoading,
  statsError,
  brandTheme,
}: SectionRendererProps) {
  const logger = useMolecule(LoggerMolecule)

  useEffect(() => {
    const sectionContext: EventContext = {
      sectionId: section.id,
      sectionType: section.type,
      sectionOrder: section.order.toString(),
      merchantBlockchain: merchantBlockchain || "ethereum",
      flow: Flow.BrandPage,
      isVisible: section.isVisible.toString(),
    }

    logger?.api.pushEvent("Brand section viewed", sectionContext)
    amplitude.track("Brand section viewed", sectionContext)
  }, [
    section.id,
    section.type,
    section.order,
    section.isVisible,
    merchantBlockchain,
    logger,
  ])

  // Don't render invisible sections
  if (!section.isVisible) {
    return null
  }

  // Render the appropriate section type
  switch (section.type) {
    case "info":
      return (
        <InfoSection
          props={
            section.props as {
              title: string
              description: string
              image?: string
              direction: "ltr" | "rtl"
              ctaButton?: { text: string; url: string }
            }
          }
          styles={section.styles}
        />
      )

    case "list":
      return (
        <ListSection
          props={
            section.props as {
              title: string
              description?: string
              layout:
                | "media"
                | "merch"
                | "collectors"
                | "collections"
                | "characters"
              columns?: 2 | 3 | 4 | 6
              items: Array<{
                id: string
                title: string
                description?: string
                image?: string
                collectionId?: string
                blockchain?: string
                ctaText?: string
                ctaUrl?: string
                metadata?: Record<string, unknown>
                link?: string
              }>
              ctaText?: string
              ctaLink?: string
              spacing?: "default" | "large"
            }
          }
          styles={section.styles}
          merchantBlockchain={merchantBlockchain}
        />
      )

    case "slider":
      return (
        <SliderSection
          props={
            section.props as {
              title?: string
              description?: string
              slides: Array<{
                id: string
                title: string
                description?: string
                backgroundImage?: string
                collectionImage?: string
                collectionId?: string
                blockchain?: string
                metadata?: Record<string, unknown>
              }>
            }
          }
          styles={section.styles}
          merchantBlockchain={merchantBlockchain}
        />
      )

    case "collections":
      return (
        <CollectionsSection
          props={
            section.props as {
              title: string
              description?: string
              layout?: "grid" | "list"
              columns?: 2 | 3 | 4
              selectedCollections?: Array<{
                id: string
                blockchain: string
                address: string
              }>
              collections?: Array<{
                _id: string
                contractAddress: string
                blockchain: string
                name: string
                description?: string
                collectionImage?: string
                status: string
                currentVersion: number
                createdAt: string
                updatedAt: string
              }>
            }
          }
          styles={section.styles}
          merchantBlockchain={merchantBlockchain}
        />
      )

    case "video": {
      const videoProps = section.props as {
        desktopVideoUrl?: string
        mobileVideoUrl?: string
        title?: string
        description?: string
        logo?: string
        socialLinks?: {
          website?: string
          twitter?: string
          discord?: string
          instagram?: string
          youtube?: string
        }
        stats?: {
          totalCollections?: number
          totalItems?: number
          totalVolume?: string
          percentListed?: string
        }
        statsLoading?: boolean
        statsError?: Error | null
        autoplay?: boolean
        muted?: boolean
        loop?: boolean
        controls?: boolean
        ctaButton?: {
          text: string
          url: string
        }
      }
      return (
        <BannerSection
          props={{
            ...videoProps,
            mediaType: "video",
            mediaUrl:
              videoProps.desktopVideoUrl || videoProps.mobileVideoUrl || "",
            // Inject stats if not provided in section props
            stats: videoProps.stats || collectionStats,
            statsLoading: videoProps.statsLoading ?? statsLoading,
            statsError: videoProps.statsError ?? statsError,
          }}
          styles={{
            ...section.styles,
            // Inject brand color if not set
            backgroundColor:
              section.styles?.backgroundColor || brandTheme?.backgroundColor,
            textColor: section.styles?.textColor || brandTheme?.textColor,
          }}
          merchantData={merchantData}
        />
      )
    }

    case "collectors":
      return (
        <CollectorsSection
          props={
            section.props as {
              title: string
              description?: string
              columns?: 2 | 3 | 4 | 6
              items: Array<{
                id: string
                title: string
                description?: string
                image?: string
                itemImage?: string
                itemUrl?: string
                collectorUrl?: string
                metadata?: Record<string, unknown>
              }>
              ctaText?: string
              ctaLink?: string
            }
          }
          styles={section.styles}
        />
      )

    case "banner": {
      const bannerProps = section.props as {
        mediaType: "image" | "video"
        mediaUrl: string
        desktopVideoUrl?: string
        mobileVideoUrl?: string
        title?: string
        description?: string
        logo?: string
        socialLinks?: {
          website?: string
          twitter?: string
          discord?: string
          instagram?: string
          youtube?: string
        }
        stats?: {
          totalCollections?: number
          totalItems?: number
          totalVolume?: string
          percentListed?: string
        }
        statsLoading?: boolean
        statsError?: Error | null
        autoplay?: boolean
        muted?: boolean
        loop?: boolean
        controls?: boolean
        ctaButton?: {
          text: string
          url: string
        }
      }
      return (
        <BannerSection
          props={{
            ...bannerProps,
            // Inject stats if not provided in section props
            stats: bannerProps.stats || collectionStats,
            statsLoading: bannerProps.statsLoading ?? statsLoading,
            statsError: bannerProps.statsError ?? statsError,
          }}
          styles={{
            ...section.styles,
            // Inject brand color if not set
            backgroundColor:
              section.styles?.backgroundColor || brandTheme?.backgroundColor,
            textColor: section.styles?.textColor || brandTheme?.textColor,
          }}
          merchantData={merchantData}
        />
      )
    }

    case "image":
      return (
        <ImageSection
          props={
            section.props as {
              imageUrl: string
            }
          }
          styles={section.styles}
        />
      )

    case "modal":
      return (
        <ModalSection
          props={
            section.props as {
              title: string
              description: string
              ctaButton: {
                text: string
              }
              modal: {
                title: string
                content: string
              }
            }
          }
          styles={section.styles}
        />
      )

    default:
      // Fallback for unknown section types
      return (
        <div className="rounded-lg border bg-card p-6">
          <h3 className="mb-2 font-semibold text-xl">
            Unknown Section Type: {section.type}
          </h3>
          <p className="text-foreground/70 text-sm">
            This section type is not supported yet.
          </p>
          {process.env.NODE_ENV === "development" && (
            <pre className="mt-4 rounded bg-muted p-2 text-muted-foreground text-xs">
              {JSON.stringify(section, null, 2)}
            </pre>
          )}
        </div>
      )
  }
}
